import { Chip, Stack, SxProps, Theme } from '@mui/material';
import {
  EggAlt,
  Grain,
  SetMeal,
  Grass,
  WaterDrop,
  BakeryDining,
  Spa,
} from '@mui/icons-material';

const allergenIconMap: Record<string, typeof EggAlt> = {
  EGG: EggAlt,
  EGGS: EggAlt,
  GLUTEN: Grain,
  FISH: SetMeal,
  CELERY: Grass,
  MILK: WaterDrop,
  WHEAT: BakeryDining,
  SOY: Spa,
  SOYA: Spa,
  SOYBEANS: Spa,
};

export const getAllergenIcon = (code: string) => {
  return allergenIconMap[code.toUpperCase()] ?? Spa;
};

/**
 * Minimal allergen shape accepted by the component. Uses a plain string-literal status
 * (rather than importing RecipeV2's `AllergenStatus` enum) so any screen's allergen
 * status enum with matching values (Contains/MayContain/FreeFrom) can be passed in
 * directly - see Procurement Products' `ProductAllergenStatus` for another consumer.
 */
export interface AllergenChipItem {
  allergenCode: string;
  status: 'Contains' | 'MayContain' | 'FreeFrom' | 'NotPresent' | 'Unknown';
  allergenName?: string;
}

interface AllergenChipsProps {
  /** Array of allergens to render */
  allergens: AllergenChipItem[];
  /** Max chips to show before +N overflow. 0 = show all */
  max?: number;
  /** Chip size */
  size?: 'small' | 'medium';
  /** Show full allergen name instead of code */
  showName?: boolean;
  /** Spacing between chips (MUI spacing units) */
  spacing?: number;
  /** Container sx overrides */
  sx?: SxProps<Theme>;
}

/**
 * Shared allergen chip renderer - used by RecipeV2 (Build/Allergens tabs, list, detail
 * header) and Procurement Products (Allergens tab) so allergen tags look identical
 * wherever they're captured/displayed.
 */
const AllergenChips = ({
  allergens,
  max = 0,
  size = 'small',
  showName = false,
  spacing = 1,
  sx,
}: AllergenChipsProps) => {
  const visible = max > 0 ? allergens.slice(0, max) : allergens;
  const overflow = max > 0 && allergens.length > max ? allergens.length - max : 0;
  const iconSize = size === 'small' ? 14 : 18;

  return (
    <Stack
      direction="row"
      spacing={spacing}
      flexWrap="wrap"
      useFlexGap
      sx={sx}
    >
      {visible.map((a) => {
        const AllergenIcon = getAllergenIcon(a.allergenCode);
        const isContains = a.status === 'Contains';
        const isMayContain = a.status === 'MayContain';
        const label = showName && a.allergenName
          ? a.allergenName
          : a.allergenCode.charAt(0) + a.allergenCode.slice(1).toLowerCase();

        return (
          <Chip
            key={a.allergenCode}
            icon={<AllergenIcon sx={{ fontSize: iconSize }} />}
            label={label}
            size={size}
            variant="outlined"
            sx={{
              px: '1px',
              borderColor: isContains ? 'warning.main' : isMayContain ? 'orange' : 'grey.400',
              color: isContains ? 'warning.dark' : 'text.secondary',
              fontWeight: 500,
              fontSize: size === 'small' ? '0.7rem' : '0.8rem',
              '& .MuiChip-icon': {
                color: isContains ? 'warning.main' : 'grey.500',
              },
            }}
          />
        );
      })}
      {overflow > 0 && (
        <Chip
          label={`+${overflow}`}
          size={size}
          variant="outlined"
          sx={{ fontSize: size === 'small' ? '0.7rem' : '0.8rem' }}
        />
      )}
    </Stack>
  );
};

export default AllergenChips;
